import { Injectable } from '@angular/core';
import {
    DEFAULT_FRONTDOOR_ID_KEY,
    FrontdoorV3,
    FrontdoorV3Service,
    IdentityPreferencesService,
    selectFrontdoor,
    UpdateFrontdoorV3,
} from '@netfoundry-ui/shared/services';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { isEmpty } from 'lodash';

/** Cookie holding the frontdoor the FD sub-app is pointed at, so a reload lands on the same one. */
const FRONTDOOR_COOKIE = 'currentFrontdoor';

/**
 * Which frontdoor the FD sub-app is working in, and which ones it could work in.
 *
 * Its own service rather than more state on ApiService: every other console area would carry it
 * without ever reading it, and this is the only thing that scopes an FD request.
 */
@Injectable({ providedIn: 'root' })
export class FrontdoorSelectionService {
    private readonly frontdoorsSource = new BehaviorSubject<FrontdoorV3[]>([]);
    private readonly currentSource = new BehaviorSubject<FrontdoorV3 | null>(null);

    constructor(
        private frontdoorV3Service: FrontdoorV3Service,
        private identityPreferencesService: IdentityPreferencesService,
        private cookieService: CookieService
    ) {}

    /** Every frontdoor the signed-in user can reach. Empty until {@link load} answers. */
    get frontdoors$(): Observable<FrontdoorV3[]> {
        return this.frontdoorsSource.asObservable();
    }

    /** The frontdoor in use, a placeholder while one is being chosen, or null before the list lands. */
    get current$(): Observable<FrontdoorV3 | null> {
        return this.currentSource.asObservable();
    }

    get frontdoors(): FrontdoorV3[] {
        return this.frontdoorsSource.value;
    }

    get current(): FrontdoorV3 | null {
        return this.currentSource.value;
    }

    /** The id to scope requests by, or '' while nothing real is selected. */
    get currentId(): string {
        return this.currentSource.value?.id ?? '';
    }

    /** True when the user has a list to choose from and has not landed on one of them yet. */
    get needsSelection(): boolean {
        return this.frontdoors.length > 0 && isEmpty(this.currentId);
    }

    /** The frontdoor id last selected, or ''. Checked against the list before it is trusted. */
    private get storedId(): string {
        return this.cookieService.get(FRONTDOOR_COOKIE) || '';
    }

    /**
     * Read the list and point the sub-app at one. An empty list is the normal answer for a user
     * with no v3 access, so this reports no failure — the caller shows that state instead.
     */
    load(): Observable<FrontdoorV3[]> {
        const request = this.frontdoorV3Service.findFrontdoors();
        request.subscribe((frontdoors) => {
            const list = frontdoors ?? [];
            this.frontdoorsSource.next(list);
            this.currentSource.next(
                selectFrontdoor(
                    list,
                    this.storedId,
                    this.identityPreferencesService.getPreference(DEFAULT_FRONTDOOR_ID_KEY) ?? ''
                )
            );
        });
        return request;
    }

    /** Point the sub-app at `frontdoor`. A placeholder carries no id, so it cannot overwrite a real choice. */
    select(frontdoor: FrontdoorV3) {
        this.currentSource.next(frontdoor);
        if (isEmpty(frontdoor?.id)) {
            return;
        }
        this.cookieService.delete(FRONTDOOR_COOKIE, '/');
        this.cookieService.set(FRONTDOOR_COOKIE, frontdoor.id, undefined, '/');
    }

    /** Change a frontdoor, then put the stored result into the list and the selection so the picker shows it at once. */
    update(id: string, update: UpdateFrontdoorV3): Observable<FrontdoorV3> {
        return this.frontdoorV3Service.updateFrontdoor(id, update).pipe(tap((frontdoor) => this.replace(frontdoor)));
    }

    /** Delete a frontdoor, then drop it from the list. When it was the selection, select again from what is left. */
    remove(id: string): Observable<void> {
        return this.frontdoorV3Service.deleteFrontdoor(id).pipe(tap(() => this.drop(id)));
    }

    private replace(frontdoor: FrontdoorV3) {
        this.frontdoorsSource.next(this.frontdoors.map((known) => (known.id === frontdoor.id ? frontdoor : known)));
        if (this.currentId === frontdoor.id) {
            this.currentSource.next(frontdoor);
        }
    }

    private drop(id: string) {
        const remaining = this.frontdoors.filter((known) => known.id !== id);
        this.frontdoorsSource.next(remaining);
        if (this.storedId === id) {
            this.cookieService.delete(FRONTDOOR_COOKIE, '/');
        }
        // A saved default that names the deleted frontdoor would never match again, so the picker would open every session.
        let preferredId = this.identityPreferencesService.getPreference(DEFAULT_FRONTDOOR_ID_KEY) ?? '';
        if (preferredId === id) {
            preferredId = '';
            this.identityPreferencesService.setPreference(DEFAULT_FRONTDOOR_ID_KEY, '');
        }
        if (this.currentId === id) {
            this.select(selectFrontdoor(remaining, '', preferredId));
        }
    }
}
